import React, { useState } from 'react';
import { ChevronRight, BarChart3, Target, Brain, Shield, Lightbulb, CheckCircle } from 'lucide-react';
import type { AIReadinessAssessmentProps, AssessmentSection } from '../types';
import { enhanceAssessmentResults } from '../services/assessmentAnalysis';

const AIReadinessAssessment: React.FC<AIReadinessAssessmentProps> = ({ onComplete }) => {
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState<any>({});
  const [showResults, setShowResults] = useState(false);

  const sections: AssessmentSection[] = [
    {
      title: "Business Vital Signs",
      subtitle: "Current State Assessment",
      icon: <BarChart3 className="w-6 h-6" />,
      questions: [
        {
          id: "team",
          question: "How would you rate your team's efficiency and capability?",
          description: "Consider productivity, skills alignment, and operational effectiveness",
          quadrant: "operations"
        },
        {
          id: "audience", 
          question: "How engaged and responsive is your target audience?",
          description: "Think about reach, engagement rates, and brand awareness",
          quadrant: "audience"
        },
        {
          id: "leads",
          question: "How consistent and qualified is your lead generation?",
          description: "Evaluate lead quality, conversion potential, and pipeline health",
          quadrant: "revenue"
        },
        {
          id: "meetings",
          question: "How effective are your sales conversations and client meetings?",
          description: "Consider conversion rates and relationship building success",
          quadrant: "revenue"
        },
        {
          id: "content",
          question: "How strong is your content and thought leadership presence?",
          description: "Assess authority, visibility, and content consistency",
          quadrant: "brand"
        },
        {
          id: "sales",
          question: "How well is your sales process converting prospects to clients?",
          description: "Look at close rates, deal sizes, and sales cycle efficiency",
          quadrant: "revenue"
        },
        {
          id: "partnerships",
          question: "How valuable are your business partnerships and referral network?",
          description: "Consider referral quality, strategic alliances, and network growth",
          quadrant: "brand"
        },
        {
          id: "profit",
          question: "How sustainable and growing are your profit margins?",
          description: "Evaluate financial health, growth trajectory, and sustainability",
          quadrant: "revenue"
        }
      ]
    },
    {
      title: "Primary Challenge",
      subtitle: "Strategic Focus Area",
      icon: <Target className="w-6 h-6" />,
      questions: [
        {
          id: "primary_challenge",
          question: "What's your biggest business challenge right now?",
          description: "Select the area that would have the most impact on your business",
          type: "single_choice",
          options: [
            { value: "Growing revenue", label: "Growing revenue", desc: "Need to increase sales and financial performance" },
            { value: "Building brand trust", label: "Building brand trust", desc: "Establishing credibility and market recognition" },
            { value: "Expanding audience", label: "Expanding audience", desc: "Reaching new markets and growing customer base" },
            { value: "Improving operations", label: "Improving operations", desc: "Optimizing processes and team efficiency" }
          ]
        }
      ]
    },
    {
      title: "AI Experience",
      subtitle: "Current AI Readiness",
      icon: <Brain className="w-6 h-6" />,
      questions: [
        {
          id: "ai_experience",
          question: "What's your current level of AI implementation?",
          description: "Be honest about where you are today",
          type: "single_choice",
          options: [
            { value: "beginner", label: "Just Getting Started", desc: "Little to no AI experience, curious about possibilities" },
            { value: "intermediate", label: "Some Experience", desc: "Tried a few AI tools, looking to scale implementation" },
            { value: "advanced", label: "Actively Implementing", desc: "Regular AI usage, seeking strategic optimization" }
          ]
        },
        {
          id: "ai_budget",
          question: "What's your potential AI implementation budget?",
          description: "Consider your investment capacity for meaningful AI transformation",
          type: "single_choice",
          options: [
            { value: "under_25k", label: "Under $25K", desc: "Testing and pilot phase budget" },
            { value: "25k_50k", label: "$25K - $50K", desc: "Serious transformation investment" },
            { value: "over_50k", label: "$50K+", desc: "Strategic enterprise-level initiative" }
          ]
        }
      ]
    },
    {
      title: "Ethical Framework",
      subtitle: "Values Alignment",
      icon: <Shield className="w-6 h-6" />,
      questions: [
        {
          id: "ethics_importance",
          question: "How important is ethical AI implementation to your organization?",
          description: "Consider transparency, human augmentation vs replacement, and responsible innovation",
          type: "single_choice",
          options: [
            { value: "critical", label: "Absolutely Critical", desc: "Ethical considerations drive all our decisions" },
            { value: "important", label: "Very Important", desc: "Want to do the right thing while achieving results" },
            { value: "consider", label: "Worth Considering", desc: "Open to ethical approaches if they're effective" }
          ]
        }
      ]
    }
  ];

  const handleAnswer = (questionId: string, value: any) => {
    setAnswers({
      ...answers,
      [questionId]: value
    });
  };

  const calculateResults = () => {
    // Calculate vital signs scores
    const vitalSigns = {
      team: answers.team || 5,
      audience: answers.audience || 5,
      leads: answers.leads || 5,
      meetings: answers.meetings || 5,
      content: answers.content || 5,
      sales: answers.sales || 5,
      partnerships: answers.partnerships || 5,
      profit: answers.profit || 5
    };

    // Calculate quadrant scores
    const quadrantScores = {
      revenue: Math.round((vitalSigns.leads + vitalSigns.meetings + vitalSigns.sales + vitalSigns.profit) / 4 * 10) / 10,
      brand: Math.round((vitalSigns.content + vitalSigns.partnerships) / 2 * 10) / 10,
      audience: vitalSigns.audience,
      operations: vitalSigns.team
    };

    // Determine business stage
    let businessStage = "Growing";
    let primaryQuadrant = "revenue";
    
    if (vitalSigns.profit <= 4 || vitalSigns.sales <= 4) {
      businessStage = "Startup";
      primaryQuadrant = "revenue";
    } else if (vitalSigns.profit <= 6 || vitalSigns.partnerships <= 4) {
      if (answers.primary_challenge === "Building brand trust") {
        businessStage = "Growing";
        primaryQuadrant = "brand";
      } else {
        businessStage = "Growing";
        primaryQuadrant = "revenue";
      }
    } else if (vitalSigns.team <= 6 || answers.primary_challenge === "Expanding audience") {
      businessStage = "Established";
      primaryQuadrant = "audience";
    } else {
      businessStage = "Scaling";
      primaryQuadrant = "operations";
    }

    // Get recommendations based on challenge and experience
    const getRecommendations = () => {
      const challengeSolutions = {
        "Growing revenue": {
          quickWins: [
            "Set up AI-powered lead scoring system",
            "Automate follow-up sequences for prospects",
            "Implement sales conversation templates"
          ],
          aiStrategies: [
            "Lead qualification automation",
            "Sales conversation optimization", 
            "Revenue prediction analytics"
          ]
        },
        "Building brand trust": {
          quickWins: [
            "Create AI brand voice assistant for consistency",
            "Automate social proof collection",
            "Implement consistent messaging across channels"
          ],
          aiStrategies: [
            "Content consistency automation",
            "Brand voice standardization",
            "Trust amplification through transparency"
          ]
        },
        "Expanding audience": {
          quickWins: [
            "Set up audience analytics dashboard",
            "Create personalized content workflows",
            "Implement engagement scoring system"
          ],
          aiStrategies: [
            "Audience segmentation and targeting",
            "Content personalization at scale",
            "New market prediction"
          ]
        },
        "Improving operations": {
          quickWins: [
            "Automate repetitive administrative tasks",
            "Implement AI-powered standard operating procedures",
            "Set up operational efficiency tracking"
          ],
          aiStrategies: [
            "Process automation and optimization",
            "Cognitive load reduction systems",
            "Predictive operational analytics"
          ]
        }
      };

      return challengeSolutions[answers.primary_challenge as keyof typeof challengeSolutions] || challengeSolutions["Growing revenue"];
    };

    const recommendations = getRecommendations();
    const overallScore = Math.round(Object.values(vitalSigns).reduce((a, b) => a + b, 0) / 8 * 10) / 10;

    return {
      vitalSigns,
      quadrantScores,
      businessStage,
      primaryQuadrant,
      recommendations,
      overallScore,
      primaryChallenge: answers.primary_challenge,
      aiExperience: answers.ai_experience,
      aiBudget: answers.ai_budget,
      ethicsImportance: answers.ethics_importance
    };
  };

  const renderQuestion = (question: any, _sectionIndex: number, _questionIndex: number) => {
    if (question.type === "single_choice") {
      return (
        <div className="space-y-3">
          {question.options.map((option: any) => (
            <button
              key={option.value}
              onClick={() => handleAnswer(question.id, option.value)}
              className={`w-full p-4 text-left border-2 rounded-lg transition-all ${
                answers[question.id] === option.value
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="font-medium">{option.label}</div>
              <div className="text-sm text-gray-600 mt-1">{option.desc}</div>
            </button>
          ))}
        </div>
      );
    }

    // Default slider for vital signs
    return (
      <div className="space-y-4">
        <div className="flex justify-between text-sm text-gray-500">
          <span>Needs Work</span>
          <span>Excellent</span>
        </div>
        <input
          type="range"
          min="1"
          max="10"
          value={answers[question.id] || 5}
          onChange={(e) => handleAnswer(question.id, parseInt(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
        />
        <div className="text-center">
          <span className="text-2xl font-bold text-blue-600">
            {answers[question.id] || 5}/10
          </span>
        </div>
      </div>
    );
  };

  const renderResults = () => {
    const baseResults = calculateResults();
    const results = enhanceAssessmentResults(baseResults);
    
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Your AI Readiness Report</h1>
          <p className="text-lg text-gray-600">Based on the Amazon Best-Selling "Amplify AI" Book Framework</p>
          <p className="text-sm text-gray-500">by Ronsley Seriojo Vaz - SymbioEthical AI Implementation</p>
        </div>

        {/* Overall Score */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-6 rounded-xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Overall Business Health</h2>
              <p className="text-blue-100">Across all vital signs</p>
            </div>
            <div className="text-4xl font-bold">{results.overallScore}/10</div>
          </div>
        </div>

        {/* Business Stage & Primary Focus */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white p-6 border-2 border-gray-200 rounded-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Business Stage</h3>
            <div className="text-2xl font-bold text-blue-600 mb-2">{results.businessStage}</div>
            <p className="text-gray-600">Based on your vital signs analysis</p>
          </div>
          <div className="bg-white p-6 border-2 border-gray-200 rounded-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Primary Focus Area</h3>
            <div className="text-2xl font-bold text-green-600 mb-2 capitalize">{results.primaryQuadrant}</div>
            <p className="text-gray-600">Where AI can have the biggest impact</p>
          </div>
        </div>

        {/* Quadrant Scores */}
        <div className="bg-white p-6 border-2 border-gray-200 rounded-xl">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Growth Quadrant Analysis</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(results.quadrantScores).map(([quadrant, score]) => (
              <div key={quadrant} className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{score}</div>
                <div className="text-sm text-gray-600 capitalize">{quadrant}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-white p-6 border-2 border-gray-200 rounded-xl">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            <Lightbulb className="w-5 h-5 inline mr-2" />
            Recommended Quick Wins
          </h3>
          <div className="space-y-3">
            {results.recommendations.quickWins.map((win: string, index: number) => (
              <div key={index} className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <span className="text-gray-700">{win}</span>
              </div>
            ))}
          </div>
        </div>

        {/* AI Strategies */}
        <div className="bg-white p-6 border-2 border-gray-200 rounded-xl">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Strategic AI Implementation Areas</h3>
          <div className="space-y-3">
            {results.recommendations.aiStrategies.map((strategy: string, index: number) => (
              <div key={index} className="flex items-start">
                <Brain className="w-5 h-5 text-purple-500 mt-0.5 mr-3 flex-shrink-0" />
                <span className="text-gray-700">{strategy}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Book Reference */}
        <div className="bg-gradient-to-r from-brand-red/10 to-brand-beige/20 p-6 rounded-xl border border-brand-beige">
          <div className="text-center mb-4">
            <h4 className="font-bold text-brand-black mb-2">📚 Want to dive deeper into the Amplify AI Framework?</h4>
            <p className="text-brand-gray text-sm mb-4">
              This assessment is based on Ronsley Vaz's comprehensive book "Amplify AI: Integrating Intelligence, Preserving Humanity"
            </p>
            <a 
              href="https://amplifyais.com/book" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center bg-brand-red text-brand-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
            >
              Get the Full Framework Book
            </a>
          </div>
        </div>

        {/* Next Steps CTA */}
        <div className="bg-gradient-to-r from-green-500 to-blue-600 text-white p-8 rounded-xl text-center">
          <h3 className="text-2xl font-bold mb-2">Ready to Transform Your Business?</h3>
          <p className="text-green-100 mb-6">
            This assessment is just the beginning. Get your complete 15-page AI Implementation Roadmap 
            and discover how companies like yours have achieved {results.costModel ? `${results.costModel.roi.month12}X` : '10X'} ROI 
            with the proven SymbioEthical Framework.
          </p>
          <button 
            onClick={() => onComplete(results)}
            className="bg-white text-blue-600 px-8 py-3 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors"
          >
            Get My Complete Roadmap
          </button>
          <p className="text-sm text-green-100 mt-2">Worth $2,500 in consulting value - Based on Amazon Best-Selling Book</p>
        </div>
      </div>
    );
  };

  if (showResults) {
    return renderResults();
  }

  const currentSectionData = sections[currentSection];
  const totalQuestions = sections.reduce((sum, section) => sum + section.questions.length, 0);
  const answeredQuestions = Object.keys(answers).length;
  const progress = (answeredQuestions / totalQuestions) * 100;

  return (
    <div className="max-w-2xl mx-auto p-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Readiness Assessment</h1>
        <p className="text-gray-600">Based on the Amazon Best-Selling "Amplify AI" Book Framework</p>
        <p className="text-sm text-gray-500">SymbioEthical AI Implementation by Ronsley Seriojo Vaz</p>
        
        {/* Progress Bar */}
        <div className="mt-6">
          <div className="flex justify-between text-sm text-gray-500 mb-2">
            <span>Progress</span>
            <span>{Math.round(progress)}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Section Header */}
      <div className="bg-white p-6 border-2 border-gray-200 rounded-xl mb-6">
        <div className="flex items-center mb-2">
          {currentSectionData.icon}
          <h2 className="text-xl font-bold text-gray-900 ml-2">{currentSectionData.title}</h2>
        </div>
        <p className="text-gray-600">{currentSectionData.subtitle}</p>
      </div>

      {/* Questions */}
      <div className="space-y-6">
        {currentSectionData.questions.map((question, questionIndex) => (
          <div key={question.id} className="bg-white p-6 border-2 border-gray-200 rounded-xl">
            <h3 className="text-lg font-medium text-gray-900 mb-2">{question.question}</h3>
            <p className="text-gray-600 text-sm mb-4">{question.description}</p>
            {renderQuestion(question, currentSection, questionIndex)}
          </div>
        ))}
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-8">
        <button
          onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
          disabled={currentSection === 0}
          className="px-6 py-2 text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        
        {currentSection === sections.length - 1 ? (
          <button
            onClick={() => setShowResults(true)}
            disabled={answeredQuestions < totalQuestions}
            className="px-8 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            Get My Results
            <ChevronRight className="w-4 h-4 ml-2" />
          </button>
        ) : (
          <button
            onClick={() => setCurrentSection(Math.min(sections.length - 1, currentSection + 1))}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-2" />
          </button>
        )}
      </div>
    </div>
  );
};

export default AIReadinessAssessment;